import { AbstractType, Signal, TemplateRef, Type } from '@angular/core';
import { OneOrMany, SchemaPath, ValidationError } from '@angular/forms/signals';
import {
  Entity,
  IsoDate,
  IsoDateTime,
  IsoDateTimeTz,
  IsoTime,
  IsoTimeTz,
  MutationRepository,
  Path,
  QueryFunction,
} from '@softline/core';
import { Translation } from '@softline/ui-core';
import { GridCellRenderer } from '../abstraction/grid-cell-renderer';
import { DataSource } from '@softapps/core';

export interface SelectableRow<TRow extends object> {
  type: 'single' | 'multi';
  /** Derives each row's checkbox state from row data (e.g. a `selected`
   *  field persisted by the backend) instead of relying only on AG Grid's
   *  own internal selection state. Re-synced whenever the row data changes. */
  isSelected?: (row: TRow) => boolean;
}

/** Emitted by `GridComponent.rowClickSelect` on a row click, carrying the
 *  native click's `shiftKey` and the row's index — enough for a consumer to
 *  implement its own click/shift-click range-select independent of AG Grid's
 *  native `rowSelection`. */
export interface RowClickSelectEvent<TRow extends object> {
  row: TRow;
  rowIndex: number | null;
  shiftKey: boolean;
}

export interface InsertRow<TRow extends object> {
  mutations: Required<Pick<MutationRepository<TRow>, 'create'>>;
  empty: () => TRow;
}

export interface EditRow<TRow extends object> {
  mutations: Required<Pick<MutationRepository<TRow>, 'update'>>;
}

export interface DeleteRow<TRow extends object> {
  mutations: Required<Pick<MutationRepository<TRow>, 'delete'>>;
}

export interface ValidateRow<TRow extends object> {
  validate: (schema: SchemaPath<TRow>) => void;
}

export interface Tree<TRow extends object> {
  options?: TreeOptions<TRow>;
  getTreePath: GetTreePath<TRow>;
}

/** Consumer-defined entry in the grid's row context menu. */
export interface GridRowAction<TRow extends object> {
  label: string;
  /** Font Awesome classes, e.g. 'fa-regular fa-copy'. */
  icon?: string;
  /** Defaults to enabled; a false return renders the entry disabled. */
  enabled?: (row: TRow) => boolean;
  /** Defaults to visible; a false return drops the entry for that row. Use for
   *  actions a row cannot have at all — `enabled` for ones it may regain. */
  visible?: (row: TRow) => boolean;
  action: (row: TRow) => void;
}

export interface GridRowActions<TRow extends object> {
  actions: GridRowAction<TRow>[];
  /** Drops AG's default context-menu entries (copy/paste/charts/export) — the
   *  menu then holds only the registered row actions plus the wrapper's own
   *  insert/delete entries where those are configured. */
  suppressDefaults?: boolean;
}

export interface Grid<TRow extends object> {
  dataSource: DataSource<TRow>;
  columns: Signal<Column<TRow>[]>;
  detail: DetailFactory<TRow, object> | null;
  detailOptions: DetailOptions<TRow, object> | null;
  options?: GridOptions<TRow>;

  insertRow: InsertRow<TRow> | null;
  editRow: EditRow<TRow> | null;
  deleteRow: DeleteRow<TRow> | null;
  validate: ValidateRow<TRow> | null;
  tree: Tree<TRow> | null;
  selectable: SelectableRow<TRow> | null;
  rowActions: GridRowActions<TRow> | null;
  /** Optional: returns a CSS class (or array of classes) applied to each row.
      Receives the row data; called by AG Grid via `getRowClass`. */
  getRowClass?: (row: TRow) => string | string[] | null | undefined;
}

export interface TreeOptions<TRow extends object> {
  header?: Signal<Translation>;
  defaultOpen?: boolean;
  getValue?: (row: TRow) => unknown;
}

export interface GridOptions<TRow extends object> {
  rowGroupPanelShow?: 'always' | 'onlyWhenGrouping' | 'never';
}

export interface DetailOptions<TMaster extends object, TDetail extends object> {
  detailRowHeight?: number | 'auto';
  masterColumn?: string | readonly string[];
}

export interface GetTreeDataPath<TRow extends object> {
  type: 'path';
  getPath: (row: TRow) => string[];
}

export interface GetTreeChildField<TRow extends object> {
  type: 'childField';
  childrenField: Path<TRow>;
}

export interface GetTreeParentField<TRow extends object> {
  type: 'parentField';
  parentIdField: Path<TRow>;
}

export type GetTreePath<TRow extends object> =
  GetTreeDataPath<TRow> | GetTreeChildField<TRow> | GetTreeParentField<TRow>;

export type DetailFactory<TMaster extends object, TRow extends object> = (
  parent: TMaster
) => Grid<TRow> | Type<{ master: TMaster }> | TemplateRef<any>;

export type DetailFactoryType<TMaster extends object, TRow extends object> = (
  parent: TMaster
) => Type<{ parent: TMaster }>;

export interface ValueColumnBase<TRow extends object, TValue> {
  colId: string;
  type: string;
  header: Signal<Translation>;
  getValue: (row: TRow) => TValue;
  setValue?: (row: TRow, value: TValue) => boolean;
  additionalQueryColumns?: string[];
  cellClass?: string;
  /** Inline style applied to every cell of this column (AG Grid `cellStyle`),
   *  e.g. `{ display: 'flex', alignItems: 'center' }` to vertically center
   *  content in autoHeight rows. */
  cellStyle?: Record<string, string>;
  /** Horizontal alignment of the cell content. Numeric columns default to
   *  `'right'`; set `'left'` for identifier-style numbers (e.g. Belegnummer). */
  align?: 'left' | 'right';
  headerClass?: string;

  rowGroup?: boolean;
  enableRowGroup?: boolean;

  pivot?: boolean;
  enablePivot?: boolean;

  hide?: boolean;
  sort?: 'asc' | 'desc';

  pinned?: 'left' | 'right' | boolean;
  lockPosition?: 'left' | 'right' | boolean;
  width?: number;
  resizable?: boolean;
  /** Allow / forbid sorting this column (AG Grid `sortable`). */
  sortable?: boolean;
  /** Set `false` to disable the column filter (no filter funnel / menu tab). */
  filter?: boolean;
  /** Hide the column's header menu button (the `⋮` icon) — for action/icon
   *  columns with no meaningful sort/filter/menu. */
  suppressHeaderMenuButton?: boolean;

  cellRenderer?: AbstractType<GridCellRenderer<TRow, TValue>> | string;

  queryFunction?: QueryFunction<TValue>;

  readonly?: boolean | ((row: TRow) => boolean);
  editComponent?: AbstractType<GridCellRenderer<TRow, TValue>>;
  validate?: (
    value: TValue,
    row: TRow
  ) => ValidationError | undefined | (ValidationError | undefined)[];
}

export type Column<TRow extends object> =
  | BooleanColumn<TRow>
  | StringColumn<TRow>
  | NumberColumn<TRow>
  | DateColumn<TRow>
  | DateTimeColumn<TRow, IsoDateTime | IsoDateTimeTz>
  | GroupColumn<TRow>
  | ObjectColumn<TRow, object>
  | AmountColumn<TRow>
  | QuantityColumn<TRow>
  | EntityColumn<TRow, Entity>;
export type ValueColumn<TRow extends object> =
  | BooleanColumn<TRow>
  | StringColumn<TRow>
  | NumberColumn<TRow>
  | DateColumn<TRow>
  | DateTimeColumn<TRow, IsoDateTime | IsoDateTimeTz>
  | ObjectColumn<TRow, object>
  | AmountColumn<TRow>
  | QuantityColumn<TRow>
  | EntityColumn<TRow, Entity>;

export interface GroupColumnChild {
  columnGroupShow?: 'open' | 'closed';
}

export interface StringColumn<TRow extends object> extends ValueColumnBase<TRow, string> {
  type: 'string';
  aggFunc?: string;
  format?: (value: string) => string | Signal<string>;
  multiline?: boolean;
}

export interface NumberColumn<TRow extends object> extends ValueColumnBase<TRow, number> {
  type: 'number';
  aggFunc?: string;
  format?: string;
  preventNumberDefault?: boolean;
}

export interface BooleanColumn<TRow extends object> extends ValueColumnBase<
  TRow,
  number
> {
  type: 'boolean';
  aggFunc?: string;
}

export interface AmountColumn<TRow extends object> extends ValueColumnBase<TRow, number> {
  type: 'amount';
  getCurrency: (row: TRow | undefined) => string | undefined;
  aggFunc?: string;
  format?: string;
}

export interface QuantityColumn<TRow extends object> extends ValueColumnBase<
  TRow,
  number
> {
  type: 'quantity';
  getUnit?: (row: TRow | undefined) => string | undefined;
  aggFunc?: string;
  format?: string;
}

export interface DateColumn<TRow extends object> extends ValueColumnBase<TRow, IsoDate> {
  type: 'date';
  format?: string;
}

export interface DateTimeColumn<
  TRow extends object,
  TValue extends IsoDateTime | IsoDateTimeTz | null,
> extends ValueColumnBase<TRow, TValue> {
  type: 'dateTime';
  format?: string;
}

export interface TimeColumn<
  TRow extends object,
  TValue extends IsoTime | IsoTimeTz | null,
> extends ValueColumnBase<TRow, TValue> {
  type: 'time';
  format?: string;
}

export interface ObjectColumn<
  TRow extends object,
  TValue extends object,
> extends ValueColumnBase<TRow, TValue> {
  type: 'object';
  format: (value: TValue) => string | Signal<string>;
  sortBy?: (a: TValue) => number | string;
}

export interface GroupColumn<TRow extends object> {
  type: 'group';
  header: Signal<Translation>;
  headerClass?: string | null;
  children: (Column<TRow> & GroupColumnChild)[];
  hide?: boolean;
}

export interface EntityColumn<
  TRow extends object,
  TValue extends Entity,
> extends ValueColumnBase<TRow, TValue> {
  type: 'entity';
  format: (value: TValue) => string | Signal<string>;
  sortBy?: (a: TValue) => number | string;
  children: (Column<TValue> & GroupColumnChild)[];
}

export function isValueColumn<TRow extends object, TValue>(
  col: Column<TRow>
): col is ValueColumn<TRow> {
  return (col as ValueColumn<TRow>).getValue !== undefined;
}

export function isSortableColumn<TRow extends object, TValue>(
  col: Column<TRow>
): col is ValueColumn<TRow> & { sortBy: (a: TRow) => number | string } {
  return (
    (col as ValueColumn<TRow>).getValue !== undefined && (col as any).sortBy !== undefined
  );
}
